import { PolicyRuleType } from '@abs/shared-types';
/**
 * Resolve which penalty tier is currently active and which is next.
 *
 * Tiers are sorted by absenceThreshold ascending. The last tier whose
 * threshold is ≤ the effective absence count is the active tier.
 */
export function resolveActiveTier(tiers, effectiveAbsenceCount) {
    if (tiers.length === 0) {
        return { activeTier: null, nextTier: null, absencesToNextTier: 0 };
    }
    const sorted = [...tiers].sort((a, b) => a.absenceThreshold - b.absenceThreshold);
    let activeTier = null;
    for (const tier of sorted) {
        if (effectiveAbsenceCount >= tier.absenceThreshold) {
            activeTier = tier;
        }
    }
    const nextTier = sorted.find((t) => t.absenceThreshold > effectiveAbsenceCount) ?? null;
    const absencesToNextTier = nextTier
        ? nextTier.absenceThreshold - effectiveAbsenceCount
        : 0;
    return { activeTier, nextTier, absencesToNextTier };
}
/**
 * Resolve a numeric grade to a letter grade using a grading scale.
 * Returns "?" if the scale is empty.
 */
export function resolveLetterGrade(points, scale) {
    if (scale.length === 0)
        return '?';
    // Scale is sorted descending by minPercentage (highest first)
    const sorted = [...scale].sort((a, b) => b.minPercentage - a.minPercentage);
    for (const entry of sorted) {
        if (points >= entry.minPercentage) {
            return entry.letter;
        }
    }
    // Below all thresholds
    return 'F';
}
/**
 * Compute the total grade deduction from policy tiers and effective absences.
 *
 * For PercentageBased and FixedCount rules: sum deductions from all applicable tiers.
 * For PerAbsenceDeduction: deduct per-absence rate for each penalty absence beyond grace.
 */
export function computeTotalDeduction(policy, effectiveAbsences) {
    if (policy.tiers.length === 0)
        return 0;
    if (policy.ruleType === PolicyRuleType.PerAbsenceDeduction) {
        // Per-absence deduction: first tier defines the per-absence rate
        const perAbsenceDeduction = Math.abs(policy.tiers[0].gradeDeduction);
        const penaltyAbsences = Math.max(0, effectiveAbsences.totalPenaltyAbsences);
        return perAbsenceDeduction * penaltyAbsences;
    }
    // PercentageBased or FixedCount: sum tier deductions where threshold is met
    let totalDeduction = 0;
    for (const tier of policy.tiers) {
        if (effectiveAbsences.totalPenaltyAbsences >= tier.absenceThreshold) {
            totalDeduction += Math.abs(tier.gradeDeduction);
        }
    }
    return totalDeduction;
}
//# sourceMappingURL=penaltyResolver.js.map